import {isPlatformBrowser} from '@angular/common';
import {AfterViewInit, Component, ElementRef, forwardRef, inject, Input, OnChanges, PLATFORM_ID, SimpleChanges, ViewChild} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {IonInput} from '@ionic/angular';
import type { AutocompleteTypes, TextFieldTypes } from '@ionic/core/components';

@Component({
  selector: 'sm-input',
  templateUrl: './sm-input.component.html',
  styleUrls: ['./sm-input.component.scss'],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SmInputComponent),
      multi: true,
    },
  ],
})
export class SmInputComponent implements ControlValueAccessor, AfterViewInit, OnChanges {
  private static nextId = 0;
  private readonly platformId = inject(PLATFORM_ID);
  private describedInput?: HTMLInputElement;
  readonly errorId = `sm-input-${SmInputComponent.nextId++}-error`;
  @Input() label = '';
  @Input() ariaLabel = '';
  @Input() labelOverflowTooltip = false;
  @Input() required = false;
  @Input() requiredText = '';
  @Input() errorText = '';
  @Input() type: TextFieldTypes = 'text';
  @Input() placeholder = '';
  @Input() allowedInputPattern?: RegExp;
  @Input() autocomplete: AutocompleteTypes = 'off';
  @Input() inputmode?: 'none' | 'text' | 'tel' | 'url' | 'email' | 'numeric' | 'decimal' | 'search';
  @Input() autocapitalize: 'off' | 'none' | 'on' | 'sentences' | 'words' | 'characters' = 'off';
  @Input() readonly = false;
  @Input() disabled = false;
  @Input() invalid = false;
  @Input() hasSideSlot = false;
  @Input() displayValue: string | number | null = null;
  @ViewChild(IonInput) private readonly ionInput?: IonInput;
  @ViewChild(IonInput, {read: ElementRef}) private readonly ionInputElement?: ElementRef<HTMLIonInputElement>;

  value = '';

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  ngAfterViewInit(): void {
    void this.syncErrorDescription();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['errorText'] || changes['invalid']) void this.syncErrorDescription();
  }

  private async syncErrorDescription(): Promise<void> {
    if (!this.invalid && !this.errorText && !this.describedInput) return;
    const control = this.ionInputElement?.nativeElement;
    if (!isPlatformBrowser(this.platformId) || !control?.componentOnReady) return;
    await control.componentOnReady();
    const input = await control.getInputElement();
    this.describedInput = input;
    if (this.invalid || this.errorText) input.setAttribute('aria-invalid', 'true');
    else input.removeAttribute('aria-invalid');
    if (this.errorText) input.setAttribute('aria-describedby', this.errorId);
    else input.removeAttribute('aria-describedby');
  }

  writeValue(value: string | null): void {
    this.value = value ?? '';
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  handleInput(event: CustomEvent<{ value?: string | null }>): void {
    const nextValue = event.detail.value ?? '';
    if (this.allowedInputPattern && !this.allowedInputPattern.test(nextValue)) {
      if (this.ionInput) this.ionInput.value = this.value;
      return;
    }
    this.value = nextValue;
    this.onChange(nextValue);
  }

  handleBlur(): void {
    this.onTouched();
  }

  setValueFromControl(value: string): void {
    if (this.disabled || this.readonly) return;
    this.value = value;
    this.onChange(value);
    this.onTouched();
  }

  async focus(): Promise<void> {
    await this.ionInput?.setFocus();
  }
}
